import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/dag48cptto0ceqqnhlng";

const values = [
  { icon: "ri-focus-3-line", title: "在场优先", text: "一切报道以抵达现场为起点，拒绝二手转述与空转画面。" },
  { icon: "ri-shield-check-line", title: "核实为要", text: "影像与文字相互印证，无法证实的线索绝不对外发布。" },
  { icon: "ri-lock-line", title: "保护报料人", text: "身份与联系方式默认保密，署名与否完全由你决定。" },
  { icon: "ri-eye-off-line", title: "客观克制", text: "描述事实而非煽动情绪，尊重每一位在场者的尊严。" },
];

type Status = "idle" | "success" | "error";

export default function AboutReport() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get("company_alt") || "").trim();
    const payload = new FormData();
    fd.forEach((value, key) => {
      if (key === "company_alt") return;
      payload.append(key, value);
    });

    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setSubmitting(true);
    setStatus("idle");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const msg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(String(msg || "提交失败，请稍后再试"));
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，未能提交，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="report" className="scroll-mt-28 mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        {/* about us */}
        <div>
          <p className="text-primary-600 text-xs font-semibold tracking-[0.25em] mb-2">ABOUT · 关于我们</p>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-snug">
            我们就在现场，<br className="hidden sm:block" />替你捕捉每一个瞬间
          </h2>
          <p className="mt-4 text-foreground-600 leading-relaxed text-[15px]">
            直击现场是一支扎根一线的实时爆料与现场报道团队。我们相信，真实的信息来自亲临现场的人，
            而非远端的剪辑。无论是突发的火情、街角的暖意，还是万人体育馆的呐喊，我们都坚持以在场记者的
            眼睛去记录，以第一时间把现场同步给你。
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200 bg-background-100 p-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500/10 text-primary-600 text-lg">
                  <i className={v.icon} />
                </span>
                <h3 className="mt-3 font-bold text-foreground-950 text-[15px]">{v.title}</h3>
                <p className="mt-1 text-[13px] text-foreground-600 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-lg border-l-4 border-accent-500 bg-accent-50/50 p-4">
            <div className="flex items-start gap-3">
              <i className="ri-chat-quote-line text-2xl text-accent-600 shrink-0" />
              <p className="text-foreground-700 text-sm leading-relaxed">
                「好的报道从来不是坐在屏幕前编出来的，而是站在现场，替每一个没能到场的人多看一会儿。」
              </p>
            </div>
            <p className="mt-2 text-xs text-foreground-400 text-right">—— 直击现场 · 编辑部</p>
          </div>
        </div>

        {/* report form */}
        <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
          <div className="flex items-center gap-3 mb-1">
            <span className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xl">
              <i className="ri-camera-line" />
            </span>
            <div>
              <h3 className="font-heading text-xl font-bold text-foreground-950">向现场报料</h3>
              <p className="text-xs text-foreground-500">你在场，我们即刻响应</p>
            </div>
          </div>

          <form onSubmit={onSubmit} className="mt-5 space-y-4" data-readdy-form="news-tip">
            <div className="hp-trap" aria-hidden="true">
              <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" readOnly aria-hidden="true" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-medium text-foreground-700 mb-1.5">您的称呼</label>
                <input
                  name="name"
                  type="text"
                  required
                  placeholder="例如：林先生"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-foreground-700 mb-1.5">联系方式（用于回访核实）</label>
                <input
                  name="email"
                  type="email"
                  placeholder="手机号或邮箱"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-medium text-foreground-700 mb-1.5">事件地点</label>
                <input
                  name="location"
                  type="text"
                  placeholder="尽量具体，便于记者到场"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-foreground-700 mb-1.5">事发时间</label>
                <input
                  name="datetime"
                  type="text"
                  placeholder="例如：今天 14:00 左右"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
            </div>

            <div>
              <label className="block text-[13px] font-medium text-foreground-700 mb-1.5">现场情况描述</label>
              <textarea
                name="message"
                required
                maxLength={500}
                rows={4}
                placeholder="请描述你看到的过程、细节，是否有人需要帮助等……"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
              />
              <p className="mt-1 text-[11px] text-foreground-400 text-right">最多 500 字</p>
            </div>

            {status === "success" && (
              <div className="rounded-md bg-accent-50 border border-accent-200 text-accent-800 px-4 py-3 text-sm">
                已收到你的报料，感谢你让我们更接近现场。我们会尽快核实并回访。
              </div>
            )}
            {status === "error" && (
              <div className="rounded-md bg-primary-50 border border-primary-200 text-primary-700 px-4 py-3 text-sm">
                {formError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 text-background-50 px-5 py-3 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              <i className="ri-send-plane-fill" />
              {submitting ? "提交中…" : "提交报料"}
            </button>
            <p className="text-[11px] text-foreground-400 text-center">
              提交即表示同意我们为核实与回访目的使用你的信息，内容将严格保密。
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}